import { Badge, Flex, Text } from '@chakra-ui/react';
import { FC } from 'react';

/**
 * Chips for a checklist item's document classifications, shared by the expanded
 * internal card and the agent-facing hover tooltip so a form always reads as a
 * form and a category always reads as a category.
 *
 * The two are deliberately distinguishable at a glance — forms are square and
 * monospaced (they are codes), categories are rounded, bordered pills (they are
 * prose). That distinction is load-bearing whenever a requirement allows more
 * than one value, since the list mixes both kinds.
 */

/** Category values are stored screaming-snake; only ever display them spaced. */
export const formatCategory = (value: string): string =>
  value.replaceAll('_', ' ');

export const ChecklistCategoryChip: FC<{ category: string }> = ({
  category,
}) => (
  <Badge
    bg="compliance.chip.category.bg"
    border="1px solid"
    borderColor="compliance.chip.category.border"
    borderRadius="full"
    color="compliance.chip.category.text"
  >
    {formatCategory(category)}
  </Badge>
);

export const ChecklistFormChip: FC<{ form: string }> = ({ form }) => (
  <Badge
    bg="compliance.chip.form.bg"
    borderRadius="sm"
    color="compliance.chip.form.text"
    fontFamily="mono"
  >
    {form}
  </Badge>
);

/**
 * The "Needs at least one attachment classified with: …" requirement, as a
 * single wrapping flow of label + chips.
 *
 * One `wrap` container rather than a label above a separate chip row: a
 * requirement can allow many values, and they have to spill onto following
 * lines rather than clip or force the tooltip to scroll sideways.
 *
 * Renders nothing when neither categories nor forms are known — an empty
 * requirement box says nothing worth the space.
 */
export const ChecklistRequirementLine: FC<{
  allowedDocumentCategories: string[];
  allowedForms: string[];
}> = ({ allowedDocumentCategories, allowedForms }) => {
  if (allowedDocumentCategories.length === 0 && allowedForms.length === 0) {
    return null;
  }

  return (
    <Flex align="center" gap={2} wrap="wrap">
      <Text as="span" color="compliance.text.secondary" fontSize="sm">
        Needs at least one attachment classified with:
      </Text>
      {allowedDocumentCategories.map((category) => (
        <ChecklistCategoryChip
          key={`category-${category}`}
          category={category}
        />
      ))}
      {allowedForms.length > 0 && allowedDocumentCategories.length > 0 ? (
        <Text
          as="span"
          color="compliance.text.muted"
          fontSize="xs"
          fontStyle="italic"
        >
          or
        </Text>
      ) : null}
      {allowedForms.map((form) => (
        <ChecklistFormChip key={`form-${form}`} form={form} />
      ))}
    </Flex>
  );
};
